/**
 * CrmPremiumKpiCard - Card de KPI premium com anel de progresso circular
 * 
 * Design inspirado em dashboards modernos com:
 * - Anel SVG de progresso circular ao redor do ícone
 * - Sombras elevadas e efeitos hover suaves
 * - Layout horizontal: anel à esquerda, texto à direita
 * - Cores distintas por variante (success, warning, info, purple)
 * 
 * @author Sistema
 * @version 1.1.0
 * RELEASE:
 *   2026-09-12 v1.1.0 - Adiciona prop opcional `titleClassName` para permitir
 *                       override local da cor do título sem alterar o padrão
 *                       dos demais dashboards.
 */

import { LucideIcon, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

/**
 * Props do componente CrmPremiumKpiCard
 */
interface CrmPremiumKpiCardProps {
  /** Título do KPI (ex: "Revenue", "Meta") */
  title: string;
  /** Valor principal do KPI */
  value: string | number;
  /** Ícone Lucide a ser exibido no centro do anel */
  icon: LucideIcon;
  /** Variante de cor do card */
  variant: 'success' | 'warning' | 'info' | 'purple';
  /** Progresso de 0 a 100 para o anel (opcional, default 75) */
  progress?: number;
  /** Texto informativo exibido ao hover no ícone de ajuda (?) */
  tooltip?: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Permite substituir a cor padrão do título localmente */
  titleClassName?: string;
  /** Quando definido, o card vira clicável (drill-down) */
  onClick?: () => void;
  /** Linha auxiliar opcional exibida abaixo do título (detalhe do cálculo) */
  subtitle?: string;
}

/**
 * Configurações de cores por variante
 */
const variantStyles = {
  success: {
    ringColor: '#10b981', // emerald-500
    iconColor: 'text-emerald-600',
    bgGlow: 'hover:shadow-emerald-100/50 dark:hover:shadow-emerald-900/20',
  },
  warning: {
    ringColor: '#f59e0b', // amber-500
    iconColor: 'text-amber-600',
    bgGlow: 'hover:shadow-amber-100/50 dark:hover:shadow-amber-900/20',
  },
  info: {
    ringColor: '#3b82f6', // blue-500
    iconColor: 'text-blue-600',
    bgGlow: 'hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20',
  },
  purple: {
    ringColor: '#8b5cf6', // violet-500
    iconColor: 'text-violet-600',
    bgGlow: 'hover:shadow-violet-100/50 dark:hover:shadow-violet-900/20',
  },
};

/**
 * Componente de anel de progresso SVG
 */
function ProgressRing({ 
  progress, 
  color, 
  size = 48, 
  strokeWidth = 3 
}: { 
  progress: number; 
  color: string; 
  size?: number; 
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`}
      className="transform -rotate-90"
    >
      {/* Anel de fundo (cinza claro) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/20"
      />
      {/* Anel de progresso (colorido) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

/**
 * Card de KPI Premium com anel de progresso circular
 */
export function CrmPremiumKpiCard({
  title,
  value,
  icon: Icon,
  variant,
  progress = 75,
  tooltip,
  className,
  titleClassName,
  onClick,
  subtitle,
}: CrmPremiumKpiCardProps) {
  const styles = variantStyles[variant];
  
  // Limitar progresso entre 0 e 100
  const normalizedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={cn(
        // Base - versão compacta com min-w-0 para não estourar o grid pai
        'relative bg-card rounded-xl p-2.5 sm:p-3 h-full flex flex-col justify-center w-full min-w-0 overflow-hidden',
        // Sombras premium com transição
        'shadow-lg hover:shadow-xl',
        styles.bgGlow,
        // Borda sutil
        'border border-border/50',
        // Transições suaves
        'transition-all duration-300 ease-out',
        'sm:hover:scale-[1.02] sm:hover:-translate-y-0.5',
        // Cursor
        onClick ? 'cursor-pointer' : 'cursor-default',
        className
      )}
    >
      <div className="flex items-center justify-start gap-2 h-full min-w-0">
        {/* Container do anel com ícone centralizado - versão compacta */}
        <div className="relative flex-shrink-0">
          {/* Anel de progresso - menor */}
          <ProgressRing 
            progress={normalizedProgress} 
            color={styles.ringColor}
            size={32}
            strokeWidth={2.5}
          />
          
          {/* Ícone centralizado no anel - menor */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={cn('h-3 w-3', styles.iconColor)} />
          </div>
        </div>

        {/* Conteúdo textual - versão compacta */}
        <div className="flex-1 min-w-0">
          {/* Valor principal - destaque menor */}
          <p className="text-base sm:text-xl font-bold text-foreground truncate leading-tight">
            {value}
          </p>
          
          {/* Título/Label com tooltip informativo opcional */}
          <div className="flex items-center gap-1 min-w-0">
            <p className={cn('text-[10px] sm:text-xs truncate', titleClassName ?? 'text-muted-foreground')}>
              {title}
            </p>
            {tooltip && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Detalhe auxiliar do cálculo (opcional) */}
          {subtitle && (
            <p className="text-[10px] text-muted-foreground/80 truncate">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

